import type { CrudFieldOption } from '@open-mercato/ui/backend/CrudForm'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'

const DEMO_CARGO_REFERENCES = [
  'CARGO-DEMO-CTR-001',
  'CARGO-DEMO-PAL-001',
  'CARGO-DEMO-BULK-001',
  'CARGO-DEMO-CHL-001',
] as const

type SlotCargoRecord = {
  id?: unknown
  referenceNumber?: unknown
  cargoType?: unknown
  currentMode?: unknown
}

function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function formatCargoLabel(cargo: SlotCargoRecord): string | null {
  const id = asText(cargo.id)
  const referenceNumber = asText(cargo.referenceNumber)
  if (!id || !referenceNumber) return null
  return referenceNumber
}

function formatCargoDescription(cargo: SlotCargoRecord): string | null {
  const cargoType = asText(cargo.cargoType)
  const currentMode = asText(cargo.currentMode)
  const parts = [cargoType, currentMode].filter((value): value is string => typeof value === 'string' && value.length > 0)
  return parts.length > 0 ? parts.join(' • ') : null
}

export async function loadSlotCargoFieldOptions(query?: string): Promise<CrudFieldOption[]> {
  const params = new URLSearchParams({
    pageSize: '20',
  })

  const search = asText(query)
  if (search) {
    params.set('search', search)
  }

  const payload = await readApiResultOrThrow<{ items?: SlotCargoRecord[] }>(
    `/api/freight/cargo?${params.toString()}`,
  )

  const options = (payload.items ?? [])
    .map((cargo) => {
      const value = asText(cargo.id)
      const label = formatCargoLabel(cargo)
      if (!value || !label) return null
      const description = formatCargoDescription(cargo)
      return {
        value,
        label: description ? `${label} — ${description}` : label,
      }
    })
    .filter((option): option is CrudFieldOption => option !== null)

  if (search) {
    return options
  }

  const priority = new Map<string, number>(DEMO_CARGO_REFERENCES.map((reference, index) => [reference, index]))
  return [...options].sort((left, right) => {
    const leftReference = left.label.split(' — ')[0]?.trim() ?? ''
    const rightReference = right.label.split(' — ')[0]?.trim() ?? ''
    const leftPriority = priority.get(leftReference)
    const rightPriority = priority.get(rightReference)

    if (leftPriority !== undefined && rightPriority !== undefined) return leftPriority - rightPriority
    if (leftPriority !== undefined) return -1
    if (rightPriority !== undefined) return 1
    return left.label.localeCompare(right.label)
  })
}
